/**
 * `use-active-org` — pieza 2 de MULTI_ORG_SWITCHER: expone `orgs()`,
 * `activeOrg()`/`activeOrgId()` y `setActiveOrg()`. La org activa deriva del
 * puntero de módulo VALIDADO (`lib/active-org.ts`), así que un switch re-scopea
 * reactivamente todas las instancias del hook sin recargar.
 *
 * Aislamiento (pieza 5): `assertBelongsToActiveOrg` descarta un id de entidad
 * deep-linkeada que NO pertenezca a la org activa tras un switch — evita operar
 * en la org B con una entidad arrastrada de la org A.
 */

import { createMemo } from "solid-js";
import { useSession } from "@/lib/auth";
import { api } from "@/lib/api";
import { handleApiResponse } from "@space/shared";
import { activeOrgEpoch, resolveActiveOrgId, setPreferredOrgId } from "@/lib/active-org";
import { useMyOrganizations } from "@/features/courses/hooks/use-course-data";

export interface ActiveOrgApi {
  /** Orgs del usuario (sincronizadas local-first). */
  orgs: () => { id: string; name: string | null; slug: string | null }[];
  /** id de la org activa validada (o undefined si no hay orgs). */
  activeOrgId: () => string | undefined;
  /** Fila de la org activa (o undefined). */
  activeOrg: () => { id: string; name: string | null; slug: string | null } | undefined;
  /** ¿Está cargando la lista de orgs? */
  isLoading: () => boolean;
  /** Cambia la org activa (switch EXPLÍCITO): persiste local + sesión server. */
  setActiveOrg: (orgId: string) => Promise<void>;
  /** Epoch del último switch explícito (para guards de aislamiento). */
  epoch: () => number;
}

export function useActiveOrg(): ActiveOrgApi {
  const { userId } = useSession();
  const query = useMyOrganizations();

  const orgs = () =>
    (query.data ?? []).map((o) => ({
      id: o.id,
      name: o.name ?? null,
      slug: null as string | null,
    }));

  const activeOrgId = createMemo(() =>
    resolveActiveOrgId(
      userId(),
      orgs().map((o) => o.id),
    ),
  );

  const activeOrg = () => orgs().find((o) => o.id === activeOrgId());

  const setActiveOrg = async (orgId: string) => {
    const uid = userId();
    if (!uid) {
      return;
    }
    // 1) puntero local reactivo (re-scopea todo al instante).
    setPreferredOrgId(uid, orgId, true);
    // 2) sesión server (CC-30: envuelve la respuesta para lanzar en no-2xx).
    await handleApiResponse(
      api.POST("/api/org/setActiveOrganization", { body: { organizationId: orgId } }),
    );
  };

  return {
    orgs,
    activeOrgId,
    activeOrg,
    isLoading: () => query.isLoading,
    setActiveOrg,
    epoch: activeOrgEpoch,
  };
}

/**
 * Guard de aislamiento (pieza 5): devuelve `true` si `entityOrgId` coincide con
 * la org activa. Úsalo para descartar entidades deep-linkeadas de OTRA org tras
 * un switch (no mostrar/operar datos de la org A estando en la B).
 */
export function belongsToActiveOrg(
  entityOrgId: string | undefined,
  activeOrgId: string | undefined,
): boolean {
  if (!entityOrgId || !activeOrgId) {
    return false;
  }
  return entityOrgId === activeOrgId;
}
